import { useState } from 'react';
import { updatePlace } from '../../services/api';
import { PhotoUpload } from '../UI/PhotoUpload';
import { ErrorBanner } from '../UI/ErrorBanner';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import type { Visit, VisitType } from '../../types';

interface EditVisitFormProps {
  placeId: string;
  placeName: string;
  visit: Visit;
  onSuccess: () => void;
  onCancel: () => void;
}

export function EditVisitForm({ placeId, placeName, visit, onSuccess, onCancel }: EditVisitFormProps) {
  const [visitType, setVisitType] = useState<VisitType>(visit.VisitType);
  const [soloTraveller, setSoloTraveller] = useState(visit.SoloTraveller ?? '');
  const [visitDate, setVisitDate] = useState(visit.VisitDate ?? '');
  const [notes, setNotes] = useState(visit.Notes ?? '');
  const [blobUrl, setBlobUrl] = useState<string | null>(visit.UserImageBlobURL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await updatePlace(placeId, { visitType, visitDate, notes, userImageBlobURL: blobUrl, soloTraveller: visitType === 'solo' ? (soloTraveller || null) : null });
      onSuccess();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    fontSize: 14,
    marginTop: 4,
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#616161',
    marginTop: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: '0 16px 20px' }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '14px 0 4px' }}>
        Edit Visit to {placeName}
      </h3>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <label style={labelStyle}>
        Visit type
        <select value={visitType} onChange={(e) => setVisitType(e.target.value as VisitType)} style={inputStyle}>
          <option value="solo">Solo</option>
          <option value="family">Family</option>
          <option value="group">Group</option>
          <option value="markanya">Mark & Anya</option>
        </select>
      </label>

      {visitType === 'solo' && (
        <label style={labelStyle}>
          Who travelled?
          <select value={soloTraveller} onChange={(e) => setSoloTraveller(e.target.value)} style={inputStyle}>
            <option value="">Select person...</option>
            <option value="Mark">Mark</option>
            <option value="Anya">Anya</option>
            <option value="Victoria">Victoria</option>
            <option value="Liam">Liam</option>
          </select>
        </label>
      )}

      <label style={labelStyle}>
        Date visited
        <input
          type="date"
          value={visitDate}
          onChange={(e) => setVisitDate(e.target.value)}
          style={inputStyle}
        />
      </label>

      <label style={labelStyle}>
        Notes
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="How was it? Any memories to capture?"
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </label>

      <label style={labelStyle}>Photo (optional)</label>
      <PhotoUpload onUploaded={setBlobUrl} currentUrl={blobUrl} />

      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button
          type="submit"
          disabled={submitting}
          style={{
            flex: 1,
            padding: '10px',
            background: '#2196F3',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? <LoadingSpinner size={16} /> : 'Save Changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '10px 16px',
            background: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
